import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAppSub } from "@/context/AppSubContext";

const MAX_POLLS = 8;

const PaymentResult = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const { refresh: refreshAppSub } = useAppSub();
  const isSuccess = location.pathname.includes("success");
  const params = new URLSearchParams(location.search);
  const kind = params.get("kind"); // "app" = abonnement app, "photo" = achat photo
  const isApp = kind === "app";
  const isPhoto = kind === "photo";
  const returnTo = params.get("return_to") || "/";
  const [state, setState] = useState(isSuccess ? "checking" : "cancel");

  const sessionId = params.get("session_id");

  const poll = useCallback(async (attempt) => {
    if (!sessionId) { setState("error"); return; }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        await refresh();
        if (isApp) await refreshAppSub();
        setState("paid");
        return;
      }
      if (data.status === "failed" || data.payment_status === "failed") { setState("error"); return; }
    } catch (e) {
      setState("error");
      return;
    }
    if (attempt >= MAX_POLLS) { setState("pending"); return; }
    setTimeout(() => poll(attempt + 1), 2000);
  }, [sessionId, refresh, refreshAppSub, isApp]);

  useEffect(() => {
    if (isSuccess) poll(0);
  }, [isSuccess, poll]);

  const backLabel = isApp ? "Entrer dans l'application" : isPhoto ? "Voir la photo" : "Retour au portefeuille";
  const backTo = isApp ? "/" : isPhoto ? returnTo : "/portefeuille";
  const paidSub = isApp ? "Votre abonnement est actif." : isPhoto ? "La photo a été déverrouillée." : "Votre solde a été crédité.";

  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <Card className="max-w-sm w-full p-8 rounded-2xl text-center" data-testid="payment-result">
        {state === "checking" && (
          <><Loader2 className="h-12 w-12 mx-auto animate-spin text-primary" /><p className="font-display text-lg mt-4">Validation du paiement…</p></>
        )}
        {state === "paid" && (
          <><CheckCircle2 className="h-14 w-14 mx-auto text-[hsl(152_72%_50%)]" /><p className="font-display text-xl mt-4">Paiement réussi !</p><p className="text-sm text-muted-foreground mt-1">{paidSub}</p></>
        )}
        {state === "pending" && (
          <><Loader2 className="h-12 w-12 mx-auto text-warning" /><p className="font-display text-lg mt-4">Paiement en cours de traitement</p><p className="text-sm text-muted-foreground mt-1">La confirmation arrive sous peu.</p></>
        )}
        {state === "cancel" && (
          <><XCircle className="h-14 w-14 mx-auto text-muted-foreground" /><p className="font-display text-xl mt-4">Paiement annulé</p><p className="text-sm text-muted-foreground mt-1">Aucun montant n'a été débité.</p></>
        )}
        {state === "error" && (
          <><XCircle className="h-14 w-14 mx-auto text-destructive" /><p className="font-display text-xl mt-4">Une erreur est survenue</p></>
        )}
        <Button className="w-full rounded-xl mt-6" onClick={() => navigate(backTo)} data-testid="payment-result-back">{backLabel}</Button>
      </Card>
    </div>
  );
};

export default PaymentResult;
